import 'normalize.css';
import '../styling/global.module.css';
import '@farmers-fridge/ui-library/dist/colors.module.css';
import '@farmers-fridge/ui-library/dist/fonts.module.css';
import '@farmers-fridge/ui-library/dist/typography.module.css';
import '../styling/css-modules-force-ordering.module.css';

import React, { useEffect, useState } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { CookiesProvider } from 'react-cookie';
import HeaderInformational from '../components/branding/HeaderInformational';
import FooterInformational from '../components/branding/FooterInformational';
import Banner from '../components/branding/Banner';
import SignUpModal from '../components/branding/SignUpModal';
import useModal from '../utils/useModal';
import { ContextProvider } from '../utils/ContextProvider';
import SessionIdWrapper from './SessionIdWrapper';

/** @param {string} path */
export const useModalData = path => {
  const { allContentfulModal, allContentfulSectionBanner } = useStaticQuery(
    graphql`
      query {
        allContentfulModal {
          edges {
            node {
              contentful_id
              triggerDelaySeconds
              headline
              emailPlaceholder
              phonePlacholder
              buttonCta
              image {
                gatsbyImageData
                description
              }
              helpText
              confirmationText
              apiEndpoint
              slug
              formVariant
            }
          }
        }

        allContentfulSectionBanner {
          edges {
            node {
              contentful_id
              headline
              ctaText
              modal {
                contentful_id
                headline
                emailPlaceholder
                phonePlacholder
                buttonCta
                image {
                  gatsbyImageData
                  description
                }
                helpText
                confirmationText
                apiEndpoint
                formVariant
              }
              slug
            }
          }
        }
      }
    `,
  );

  const filteredModals = allContentfulModal?.edges?.filter(
    /**
     * @param {object} params
     * @param {object} params.node
     * @param {number} params.node.triggerDelaySeconds
     * @param {string} params.node.slug
     */
    ({ node }) =>
      node.triggerDelaySeconds &&
      node.triggerDelaySeconds > 0 &&
      node.slug === path,
  );

  const filteredBanners = allContentfulSectionBanner?.edges?.filter(
    /**
     * @param {object} params
     * @param {object} params.node
     * @param {string} params.node.slug
     */
    ({ node }) => node.slug === path,
  );

  return {
    modalData: filteredModals?.length ? filteredModals[0].node : null,
    bannerData: filteredBanners?.length ? filteredBanners[0].node : null,
  };
};

/**
 *
 * @param {FF.PageTemplate} props
 */
function PageTemplate({ children, hero, mainContentClass = '', path }) {
  const [bannerVisible, setBannerVisible] = useState(false);

  // modalVisible handles modal timeout visibility, useModal handles modal close button
  const [modalVisible, setModalVisible] = useState(false);
  const [isShowing, toggle] = useModal();

  const { modalData, bannerData } = useModalData(path);

  function modalShown() {
    const cookie = document.cookie
      .split(';')
      .some(item => item.includes(modalData?.contentful_id));
    return cookie;
  }

  function showModal() {
    if (modalShown()) return;
    toggle();
    setTimeout(
      () => {
        setModalVisible(true);
      },
      Number(modalData?.triggerDelaySeconds) * 1000,
      window.sessionStorage.setItem(modalData?.contentful_id, 'true'),
    );
  }

  useEffect(() => {
    // if (!modalData) return;
    if (!modalShown() && modalData) showModal();
  }, []);

  useEffect(() => {
    const cookie = document.cookie
      .split(';')
      .some(item => item.includes(bannerData?.contentful_id));

    return cookie || !bannerData
      ? setBannerVisible(false)
      : setBannerVisible(true);
  }, []);

  return (
    <CookiesProvider>
      <ContextProvider
        defaultValues={{ contextData: { showOpaqueHeader: Boolean(!hero) } }}
      >
        <SessionIdWrapper>
          <HeaderInformational path={path} withHero={Boolean(hero)} />
          {bannerVisible && <Banner data={bannerData} />}
          {modalVisible && isShowing && (
            <SignUpModal data={modalData} toggle={toggle} />
          )}
          <main
            className={`${mainContentClass} ${bannerVisible ? 'bannerVisible' : ''}`}
            id="main-content"
          >
            {hero}
            {children}
          </main>
          <FooterInformational />
          <div id="modalElement" />
        </SessionIdWrapper>
      </ContextProvider>
    </CookiesProvider>
  );
}

export default PageTemplate;
